import React, { useCallback } from 'react';

import { List } from '@fastlessjs/backoffice-layer/dist/components/templates';
import { useAuth } from '@fastlessjs/backoffice-layer/dist/hooks';
import debug from '@fastlessjs/backoffice-layer/dist/utils/debug';
import { Button, Col, Row } from 'antd';

import { ReturnLineExtras } from './ReturnLineExtras';
import { t } from '../../config/translations';
import { ReturnOrderLine } from '../../orders/types';
import { useReturnStore } from '../../store';
import { useCreateReturn } from '../hooks';
import { returnSchema } from '../schemas';
import { buildReturnPayloadForOrder } from '../utils/buildReturnPayload';

// Register this component for debug tracking
debug.registerComponent('ReturnRequestTable');

type ReturnRequestTableProps = {
  hasTitle?: boolean;
  hasHeader?: boolean;
  clientId?: string;
  orderId?: string;
  hasButton?: boolean;
  hasBorder?: boolean;
};

const ReturnRequestTable = (props: ReturnRequestTableProps) => {
  debug.trackRender('ReturnRequestTable');
  const {
    hasTitle = false,
    hasHeader = true,
    clientId,
    orderId,
    hasButton = true,
    hasBorder = true,
  } = props;
  debug.log(
    'ReturnRequestTable',
    `Rendering for clientId: ${clientId}, orderId: ${orderId}`,
  );

  const { returnOrderLines, isSubmittingReturn } = useReturnStore();
  const { user } = useAuth();
  const { mutateAsync, isPending } = useCreateReturn({
    headers: { 'x-tenant-id': user.id },
  });

  const returnOrderLinesKey = `${clientId}/${orderId}`;
  const isSubmitLocked = isSubmittingReturn || isPending;

  const handleAskReturns = useCallback(async () => {
    if (isSubmitLocked) {
      return;
    }

    const payload = buildReturnPayloadForOrder(returnOrderLines, clientId, orderId);
    if (!payload) {
      return;
    }

    debug.log(
      'ReturnRequestTable',
      `Submitting return for order: ${payload.orderId}, client: ${payload.clientId}`,
    );

    try {
      await mutateAsync(payload);
    } catch (error) {
      debug.error('ReturnRequestTable', `Return submission failed: ${error}`);
    }
  }, [clientId, isSubmitLocked, mutateAsync, orderId, returnOrderLines]);

  const toArray = (source: Object) => {
    if (!source) return [];

    return Object.values(source);
  };

  const hasReturnOrderLines =
    returnOrderLines[returnOrderLinesKey] &&
    Object.keys(returnOrderLines[returnOrderLinesKey]).length > 0;

  return (
    <div key={`${clientId}_${orderId}`}>
      {hasReturnOrderLines && (
        <Row>
          <Col xs={24} className={hasBorder ? 'focus-container' : ''}>
            <List<ReturnOrderLine>
              data={toArray(returnOrderLines[returnOrderLinesKey])}
              title={hasTitle ? t('list.yourReturnRequest') : ''}
              hasHeader={hasHeader}
              enableSelection={false}
              org=""
              schema={returnSchema}
              idKey="nart"
              rowKey={(record: ReturnOrderLine) => `${record?.ndoc}_${record?.nart}`}
              ExtraActions={ReturnLineExtras}
            />
            {hasButton && (
              <div className="full-right">
                <Button
                  type="primary"
                  style={{ marginTop: '12px', marginRight: '24px' }}
                  onClick={handleAskReturns}
                  loading={isSubmitLocked}
                  disabled={isSubmitLocked}
                >
                  {t('button.submitReturn')}
                </Button>
              </div>
            )}
          </Col>
        </Row>
      )}
    </div>
  );
};

export { ReturnRequestTable };

// Mark this component as initialized
debug.markInitialized('ReturnRequestTable');
